import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtraditionMlaWizardDraft } from '../../types/extraditionMlaCaseRequest';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { calcAgeFromDob } from '../../utils/caseFieldValidation';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { useExtraditionMlaLookups } from '../wizard/extraditionMla/useExtraditionMlaLookups';
import { requiresCustodialAuthority } from '../wizard/extraditionMla/extraditionMlaHelpers';
import { apiDisplayValue, apiOrLookupLabel, deliveryMethodDisplay } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: ExtraditionMlaWizardDraft;
  submittedAt: string;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestExtraditionMlaCaseDetailsTab({ caseRequestId, draft, submittedAt }: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useExtraditionMlaLookups('review', draft.provinceId);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft as unknown as CriminalWizardDraft, lookups.deliveryMethods, undefined, noData),
    [draft, lookups.deliveryMethods, noData],
  );

  const accusedAge = useMemo(() => calcAgeFromDob(draft.accusedDob), [draft.accusedDob]);
  const selectedCustodyStatus = useMemo(
    () => lookups.custodyStatuses.find((s) => s.id === draft.custodyStatusId),
    [draft.custodyStatusId, lookups.custodyStatuses],
  );
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustodyStatus?.code,
  );

  const divisionUnit = apiDisplayValue(draft.submittedDivisionName);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Categorization">
        <ReviewGrid>
          <ReviewField label="File Category" value={draft.fileCategory} />
          <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
        </ReviewGrid>
        <ReviewChips items={draft.applicableAreas} />
      </ReviewSection>

      <ReviewSection step="Step 01" title="Case Intake Details">
        <ReviewGrid>
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewField label="Case Source" value={draft.caseSource} fullWidth />
          <ReviewField
            label="Country Involved"
            value={apiOrLookupLabel(draft.countryInvolvedName, draft.countryInvolvedId, lookups.countries)}
          />
          <ReviewField label="Direction of Request" value={draft.directionOfRequest} />
          <ReviewField label="Province" value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)} />
          <ReviewField label="District" value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)} />
          <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
          <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
          <ReviewField label="Case Number" value={draft.caseNumber} />
          <ReviewField label="Connected matters" value={yesNoLabel(draft.hasConnectedMatters)} />
          {draft.hasConnectedMatters === 'yes' ? (
            <>
              <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
              <ConnectedAgdFileRefReviewField
                caseRequestId={caseRequestId}
                label="Connected AGD File Reference Number"
                fileRef={draft.connectedAgdFileRefAvailable ? draft.connectedAgdFileRef : ''}
              />
            </>
          ) : null}
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Accused Person Details">
        <ReviewGrid>
          <ReviewField label="Name of the Accused" value={draft.accusedName} />
          <ReviewField
            label="Nationality"
            value={apiOrLookupLabel(
              draft.nationalityName,
              draft.nationalityId,
              lookups.nationalities.length ? lookups.nationalities : lookups.countries,
            )}
          />
          <ReviewField
            label="Country of Origin"
            value={apiOrLookupLabel(draft.countryOfOriginName, draft.countryOfOriginId, lookups.countries)}
          />
          <ReviewField label="Date of Birth" value={formatDisplayDate(draft.accusedDob)} />
          <ReviewField label="Age" value={typeof accusedAge === 'number' ? String(accusedAge) : '—'} />
          <ReviewField label="Gender" value={apiOrLookupLabel(draft.accusedGenderName, draft.accusedGenderId, lookups.genders)} />
          <ReviewField label="Passport/ID Number" value={draft.passportOrIdNumber} />
          <ReviewField
            label="Current Custody Status"
            value={apiOrLookupLabel(draft.custodyStatusName, draft.custodyStatusId, lookups.custodyStatuses)}
          />
          {showCustodialAuthority ? (
            <ReviewField label="Custodial Authority" value={draft.custodialAuthority} />
          ) : null}
          <ReviewField label="Last Known Location" value={draft.lastKnownLocation} fullWidth />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Legal & Extradition Case Details">
        <ReviewGrid>
          <ReviewField label="Arrest Warrant Available" value={yesNoLabel(draft.arrestWarrantAvailable)} />
          {draft.arrestWarrantAvailable === 'yes' ? (
            <ReviewField label="Warrant Reference" value={draft.warrantReference} />
          ) : null}
          <ReviewField label="Applicable Treaty/Arrangement" value={draft.applicableTreatyArrangement} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Timeline">
        <ReviewGrid>
          <ReviewField
            label="Urgency Level"
            value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
          />
          <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
          <ReviewField label="Urgency Justification" value={draft.urgencyJustification} fullWidth />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 05" title="Submission & Physical File Delivery">
        <ReviewGrid>
          <ReviewField label="Submitted Division/ Unit" value={divisionUnit} />
          <ReviewField
            label="Submitted by"
            value={apiDisplayValue(draft.submittedByDisplayName)}
          />
          <ReviewField
            label="Designation"
            value={apiDisplayValue(draft.submitterDesignation)}
          />
          <ReviewField label="Email Address" value={apiDisplayValue(draft.submitterEmail)} />
          <ReviewField label="Contact Number" value={apiDisplayValue(draft.submitterContactNo)} />
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewGrid>
      </ReviewSection>
    </div>
  );
}
